import React, { useEffect, useMemo, useState } from 'react';
import ModalWrapper from '../../../ModalWrapper';
import { FormSchemas, FormWrapper, CommonFooter } from '../../../FormComponent';
import { createTestCase } from '../../../../utils/api/testcase_api';
import { getModules } from '../../../../utils/api/module_apis';
import { STATUS } from '../../../../utils/api/sharedfunction';
import { DEFAULT_TYPE } from '../../../../enums/TestCaseTypes';
import { DEFAULT_STATUS } from '../../../../enums/StatusEnum';
import { DEFAULT_PRIORITY } from '../../../../enums/PriorityEnum';

const flattenModules = (nodes, parentPath = '') => {
    if (!Array.isArray(nodes)) return [];
    return nodes.flatMap((node) => {
        if (!node?.id) return [];
        const path = parentPath ? `${parentPath} > ${node.name}` : node.name;
        return [
            { value: node.id, label: path },
            ...flattenModules(node.children, path),
        ];
    });
};

export default function QuickCreateTestCaseModal({
    isOpen,
    onClose,
    onSuccess,
    onSubmit,
    isCreating = false,
    apiError = '',
    moduleOptions = [],
    modulesLoading = false,
    modulesError = '',
    initialTitle = '',
    title = 'Create New Test Case',
}) {
    const formRef = React.useRef(null);
    const [internalModules, setInternalModules] = useState([]);
    const [internalModulesLoading, setInternalModulesLoading] = useState(false);
    const [internalModulesError, setInternalModulesError] = useState('');
    const [internalApiError, setInternalApiError] = useState('');
    const [internalCreating, setInternalCreating] = useState(false);

    const usesInternalCreate = !onSubmit;

    useEffect(() => {
        if (!isOpen || !usesInternalCreate) return undefined;
        let cancelled = false;

        const fetchModules = async () => {
            setInternalModulesLoading(true);
            setInternalModulesError('');
            setInternalApiError('');
            try {
                const result = await getModules();
                if (cancelled) return;
                if (result.status === STATUS.SUCCESS && Array.isArray(result.data)) {
                    setInternalModules(flattenModules(result.data));
                } else {
                    setInternalModules([]);
                    setInternalModulesError(result.error || 'Failed to load modules');
                }
            } catch (error) {
                if (cancelled) return;
                setInternalModules([]);
                setInternalModulesError(error?.message || 'Failed to load modules');
            } finally {
                if (!cancelled) setInternalModulesLoading(false);
            }
        };

        fetchModules();
        return () => {
            cancelled = true;
        };
    }, [isOpen, usesInternalCreate]);

    const effectiveModuleOptions = useMemo(
        () => (moduleOptions.length ? moduleOptions : internalModules),
        [internalModules, moduleOptions]
    );
    const effectiveModulesLoading = modulesLoading || internalModulesLoading;
    const effectiveModulesError = modulesError || internalModulesError;
    const effectiveApiError = apiError || internalApiError;
    const effectiveCreating = isCreating || internalCreating;

    const handleSubmit = async (values) => {
        if (onSubmit) {
            await onSubmit(values);
            return;
        }

        setInternalApiError('');
        setInternalCreating(true);
        try {
            const response = await createTestCase({
                title: values.title?.trim(),
                type: DEFAULT_TYPE,
                status: DEFAULT_STATUS,
                priority: DEFAULT_PRIORITY,
                module_id: values.module_id,
            });
            if (response.status === STATUS.SUCCESS) {
                onSuccess?.(response.data || {});
                onClose?.();
            } else {
                setInternalApiError(response.error || 'Failed to create test case');
            }
        } catch (error) {
            setInternalApiError(error?.message || 'Failed to create test case');
        } finally {
            setInternalCreating(false);
        }
    };

    const fields = [
        {
            name: 'title',
            type: 'text',
            label: 'Title',
            placeholder: 'Enter test case title',
            required: true,
            autoFocus: true,
        },
        {
            name: 'module_id',
            type: 'autosuggest',
            label: 'Module',
            required: true,
            options: effectiveModuleOptions,
            loading: effectiveModulesLoading,
            helperText: effectiveModulesError || '',
            noOptionsText: effectiveModulesLoading ? 'Loading modules...' : 'No modules available',
        },
    ];

    const buttons = [
        {
            label: 'Cancel',
            variant: 'outline',
            className: 'primary',
            onClick: onClose,
        },
        {
            label: 'Create',
            variant: 'primary',
            className: 'white bg-blue600',
            onClick: () => formRef?.current?.submit(),
            disabled: effectiveCreating || effectiveModulesLoading,
            loading: effectiveCreating,
        },
    ];

    return (
        <ModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="small"
            footer={<CommonFooter buttons={buttons} align="right" />}
        >
            <FormWrapper
                ref={formRef}
                schema={FormSchemas.quickCreateTestCase}
                fields={fields}
                onSubmit={handleSubmit}
                defaultValues={{
                    title: initialTitle || '',
                    module_id: '',
                }}
            />
            {effectiveApiError && (
                <div style={{ color: '#dc2626', marginTop: 8, fontSize: 13 }}>{effectiveApiError}</div>
            )}
        </ModalWrapper>
    );
}
